'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { MOCK_AERODROMES, AerodromeInfo } from '@/lib/mockWeatherData';
import { toast } from 'sonner';

const MAX_TERRAINS = 10;
const LS_ACTIVE_KEY = 'soarcast_active_aerodrome';
const LS_RECENT_KEY = 'soarcast_recent_aerodromes';

interface AerodromeContextValue {
  selectedAerodrome: AerodromeInfo;
  setSelectedAerodrome: (aerodrome: AerodromeInfo) => void;
  recentAerodromes: AerodromeInfo[];
  removeRecentAerodrome: (icao: string) => void;
  hydrated: boolean;
}

const AerodromeContext = createContext<AerodromeContextValue>({
  selectedAerodrome: MOCK_AERODROMES[0],
  setSelectedAerodrome: () => {},
  recentAerodromes: [],
  removeRecentAerodrome: () => {},
  hydrated: false,
});

function loadFromStorage<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function saveToStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // quota exceeded — ignore
  }
}

export function AerodromeProvider({ children }: { children: React.ReactNode }) {
  const [selectedAerodrome, setSelectedAerodromeState] = useState<AerodromeInfo>(MOCK_AERODROMES[0]);
  const [recentAerodromes, setRecentAerodromes] = useState<AerodromeInfo[]>([]);
  const [hydrated, setHydrated] = useState(false);

  // Restore from localStorage on mount (client only)
  useEffect(() => {
    const savedActive = loadFromStorage<AerodromeInfo | null>(LS_ACTIVE_KEY, null);
    const savedRecent = loadFromStorage<AerodromeInfo[]>(LS_RECENT_KEY, []);

    if (savedActive && savedActive.icao && typeof savedActive.lat === 'number' && typeof savedActive.lon === 'number') {
      setSelectedAerodromeState(savedActive);
    }
    if (Array.isArray(savedRecent)) {
      setRecentAerodromes(savedRecent);
    }
    setHydrated(true);
  }, []);

  const setSelectedAerodrome = useCallback((aerodrome: AerodromeInfo) => {
    setSelectedAerodromeState(aerodrome);
    saveToStorage(LS_ACTIVE_KEY, aerodrome);

    setRecentAerodromes((prev) => {
      // Remove duplicate if already in list
      const filtered = prev.filter((a) => a.icao !== aerodrome.icao);
      // Prepend new terrain
      const updated = [aerodrome, ...filtered];

      if (updated.length > MAX_TERRAINS) {
        // FIFO: remove the oldest (last element)
        const trimmed = updated.slice(0, MAX_TERRAINS);
        saveToStorage(LS_RECENT_KEY, trimmed);
        toast.warning('Limite de 10 terrains atteinte. Le terrain le plus ancien a été retiré.', {
          duration: 4000,
          id: 'terrain-limit',
        });
        return trimmed;
      }

      saveToStorage(LS_RECENT_KEY, updated);
      return updated;
    });
  }, []);

  const removeRecentAerodrome = useCallback((icao: string) => {
    setRecentAerodromes((prev) => {
      const updated = prev.filter((a) => a.icao !== icao);
      saveToStorage(LS_RECENT_KEY, updated);
      return updated;
    });
  }, []);

  return (
    <AerodromeContext.Provider
      value={{ selectedAerodrome, setSelectedAerodrome, recentAerodromes, removeRecentAerodrome, hydrated }}
    >
      {children}
    </AerodromeContext.Provider>
  );
}

export function useAerodrome(): AerodromeContextValue {
  return useContext(AerodromeContext);
}
